import React, { useState, useEffect, useCallback } from 'react';
import {
  Input, Button, Card, Typography, Space, List, Avatar, Tag, Alert, Spin,
  Modal, Form, message, Divider, Select, InputNumber
} from 'antd';
import {
  SearchOutlined, UserOutlined, PhoneOutlined, PlusOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  setSearchResults,
  setSearchLoading,
  setSearchError,
  clearSearchResults,
  setSelectedPatient,
  clearSelectedPatient
} from '../store/slices/patientSlice';
import {
  setPrescriptionList,
  setPrescriptionLoading,
  setPrescriptionError,
  clearPrescriptionData
} from '../store/slices/prescriptionSlice';
import PatientService, { Patient, PatientCreateData } from '../services/patientService';
import PrescriptionService from '../services/prescriptionService';
import PrescriptionDetails from './PrescriptionDetails';
import PrescriptionList from './PrescriptionList';
import { useDebouncedSearch } from '../hooks/useDebounce';

const { Title, Text } = Typography;

interface PatientSearchProps {
  onPatientSelect?: (patient: Patient) => void;
  showCreateOption?: boolean;
  placeholder?: string;
}

const PatientSearch: React.FC<PatientSearchProps> = ({
  onPatientSelect,
  showCreateOption = true,
  placeholder = "Search patient by name or phone number..."
}) => {
  const dispatch = useDispatch();
  const { searchResults, searchLoading, searchError, selectedPatient } = useSelector(
    (state: RootState) => state.patient
  );

  const [patientIdSearch, setPatientIdSearch] = useState<string>('');
  const [nameSearch, setNameSearch] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [isClearingFields, setIsClearingFields] = useState<boolean>(false);
  const [form] = Form.useForm();

  // Debounce search texts to avoid searching on every keystroke
  const debouncedPatientIdSearch = useDebouncedSearch(patientIdSearch, 500);
  const debouncedNameSearch = useDebouncedSearch(nameSearch, 500);

  const searchPatientsById = useCallback(async (patientId: string) => {
    dispatch(setSearchLoading(true));
    dispatch(setSearchError(null));

    try {
      const response = await PatientService.searchByPatientId(patientId);
      const results = (response.data || []).map((patient: any) => ({
        user_id: patient.attributes.user_id,
        name: patient.attributes.name,
        phoneNumber: patient.attributes.phone_number,
        found: true
      }));

      dispatch(setSearchResults(results));
      setShowResults(true);
    } catch (error: any) {
      console.error('Patient ID search error:', error);
      dispatch(setSearchError(error.message || 'Failed to search patients by ID'));
    } finally {
      dispatch(setSearchLoading(false));
    }
  }, [dispatch]);

  const searchPatientsByName = useCallback(async (name: string) => {
    dispatch(setSearchLoading(true));
    dispatch(setSearchError(null));

    try {
      const response = await PatientService.searchByName(name);
      const results = (response.data || []).map((patient: any) => ({
        user_id: patient.attributes.user_id,
        name: patient.attributes.name,
        phoneNumber: patient.attributes.phone_number,
        found: true
      }));

      dispatch(setSearchResults(results));
      setShowResults(true);
    } catch (error: any) {
      console.error('Patient name search error:', error);
      dispatch(setSearchError(error.message || 'Failed to search patients by name'));
    } finally {
      dispatch(setSearchLoading(false));
    }
  }, [dispatch]);

  // Search patients when debounced text changes
  useEffect(() => {
    if (isClearingFields) {
      // Don't search when we're clearing fields programmatically
      return;
    }
    
    if (debouncedPatientIdSearch.trim().length >= 2) {
      searchPatientsById(debouncedPatientIdSearch.trim());
    } else if (debouncedNameSearch.trim().length >= 2) {
      searchPatientsByName(debouncedNameSearch.trim());
    } else {
      dispatch(clearSearchResults());
      setShowResults(false);
    }
  }, [debouncedPatientIdSearch, debouncedNameSearch, dispatch, searchPatientsById, searchPatientsByName, isClearingFields]);

  const handlePatientSelect = async (patient: Patient) => {
    dispatch(setSelectedPatient(patient));
    setShowResults(false);
    // Update both fields with the selected patient data
    setIsClearingFields(true);
    setPatientIdSearch(patient.user_id);
    setNameSearch(patient.name);
    setIsClearingFields(false);
    
    // Fetch prescription list for the selected patient
    try {
      dispatch(setPrescriptionLoading(true));
      dispatch(setPrescriptionError(null));
      
      const prescriptionListResponse = await PrescriptionService.getPatientPrescriptions(patient.user_id);
      dispatch(setPrescriptionList(prescriptionListResponse.data.prescriptions));
      
    } catch (error: any) {
      console.error('Error fetching prescription list:', error);
      dispatch(setPrescriptionError(error.message || 'Failed to fetch prescription list'));
      // Clear prescription data on error
      dispatch(setPrescriptionList(null));
    } finally {
      dispatch(setPrescriptionLoading(false));
    }
    
    if (onPatientSelect) {
      onPatientSelect(patient);
    }
  };

  const handleClearSelection = () => {
    dispatch(clearSelectedPatient());
    dispatch(clearPrescriptionData());
    setIsClearingFields(true);
    setPatientIdSearch('');
    setNameSearch('');
    setIsClearingFields(false);
    setShowResults(false);
  };

  const handleCreatePatient = async (values: PatientCreateData) => {
    try {
      const patient = await PatientService.findOrCreatePatient(values);
      dispatch(setSelectedPatient(patient));
      setCreateModalVisible(false);
      // Update both fields with the created/found patient data
      setIsClearingFields(true);
      setPatientIdSearch(patient.user_id);
      setNameSearch(patient.name);
      setIsClearingFields(false);
      
      message.success(patient.created ? 'Patient created successfully' : 'Patient found and selected');
      
      if (onPatientSelect) {
        onPatientSelect(patient);
      }
      
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Failed to create/find patient');
    }
  };


  const formatPhoneNumber = (phone: string) => {
    // Format phone number for display
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    return phone;
  };

  return (
    <div style={{ position: 'relative' }}>
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
              <UserOutlined /> Patient Search
            </Title>
            {selectedPatient && (
              <Alert
                message={`Selected: ${selectedPatient.name}`}
                description={`Phone: ${formatPhoneNumber(selectedPatient.phoneNumber)}`}
                type="success"
                showIcon
                closable
                onClose={handleClearSelection}
                style={{ marginBottom: '8px' }}
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Input
              placeholder="Search by Patient ID..."
              prefix={<SearchOutlined />}
              value={patientIdSearch}
              onChange={(e) => {
                setIsClearingFields(true);
                setPatientIdSearch(e.target.value);
                if (e.target.value && nameSearch) {
                  setNameSearch(''); // Clear name search when ID is being typed
                }
                setIsClearingFields(false);
              }}
              style={{ flex: 1 }}
              allowClear
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowResults(true);
                }
              }}
            />
            <Input
              placeholder="Search by Name..."
              prefix={<UserOutlined />}
              value={nameSearch}
              onChange={(e) => {
                setIsClearingFields(true);
                setNameSearch(e.target.value);
                if (e.target.value && patientIdSearch) {
                  setPatientIdSearch(''); // Clear ID search when name is being typed
                }
                setIsClearingFields(false);
              }}
              style={{ flex: 1 }}
              allowClear
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowResults(true);
                }
              }}
            />
            {showCreateOption && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
                title="Create New Patient"
              >
                New
              </Button>
            )}
          </div>

          {searchLoading && (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <Spin size="small" /> <Text type="secondary">Searching patients...</Text>
            </div>
          )}

          {searchError && (
            <Alert
              message="Search Error"
              description={searchError}
              type="error"
              showIcon
              closable
              onClose={() => dispatch(setSearchError(null))}
            />
          )}

          {showResults && searchResults.length === 0 && !searchLoading && (
            <Alert
              message="No patients found"
              description={
                patientIdSearch ? 
                  "Try searching with a different Patient ID or use the Name field" :
                  "Try searching with a different name or use the Patient ID field"
              }
              type="info"
              showIcon
              action={
                <Button 
                  size="small" 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Create New Patient
                </Button>
              }
            />
          )}
        </Space>
      </Card>

      {/* Search Results */}
      {showResults && searchResults.length > 0 && (
        <Card
          size="small"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          <List
            size="small"
            dataSource={searchResults}
            renderItem={(patient) => (
              <List.Item
                key={patient.user_id}
                style={{ cursor: 'pointer', padding: '8px 12px' }}
                onClick={() => handlePatientSelect(patient)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={
                    <Space>
                      <Text strong>{patient.name}</Text>
                      <Tag icon={<PhoneOutlined />} color="blue">
                        {formatPhoneNumber(patient.phoneNumber)}
                      </Tag>
                    </Space>
                  }
                  description={
                    <Space>
                      <Text type="secondary">ID: {patient.user_id}</Text>
                      {patient.found && <Tag color="green">Existing</Tag>}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Create Patient Modal */}
      <Modal
        title="Create New Patient"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreatePatient}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="name"
              label="Patient Name"
              rules={[
                { required: true, message: 'Please enter patient name' },
                { min: 2, message: 'Name must be at least 2 characters' }
              ]}
            >
              <Input placeholder="Enter patient full name" />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label="Phone Number"
              rules={[
                { required: true, message: 'Please enter phone number' },
                { pattern: /^[\d+\-\s()]{10,15}$/, message: 'Please enter a valid phone number' }
              ]}
            >
              <Input placeholder="e.g., +91 98765 43210 or 9876543210" />
            </Form.Item>
          </div>

          <Divider>Optional Information</Divider>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="age"
              label="Age"
              rules={[{ type: 'number', min: 0, max: 150, message: 'Please enter a valid age' }]}
            >
              <InputNumber placeholder="Age" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="gender"
              label="Gender"
            >
              <Select placeholder="Select gender" allowClear>
                <Select.Option value="male">Male</Select.Option>
                <Select.Option value="female">Female</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="weight_in_kg"
              label="Weight (kg)"
              rules={[{ type: 'number', min: 0, max: 500, message: 'Please enter a valid weight' }]}
            >
              <InputNumber placeholder="Weight in kg" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="height_in_cms"
              label="Height (cm)"
              rules={[{ type: 'number', min: 0, max: 300, message: 'Please enter a valid height' }]}
            >
              <InputNumber placeholder="Height in cm" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="blood_group"
              label="Blood Group"
            >
              <Select placeholder="Select blood group" allowClear>
                <Select.Option value="A+">A+</Select.Option>
                <Select.Option value="A-">A-</Select.Option>
                <Select.Option value="B+">B+</Select.Option>
                <Select.Option value="B-">B-</Select.Option>
                <Select.Option value="AB+">AB+</Select.Option>
                <Select.Option value="AB-">AB-</Select.Option>
                <Select.Option value="O+">O+</Select.Option>
                <Select.Option value="O-">O-</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="martial_status"
              label="Marital Status"
            >
              <Select placeholder="Select marital status" allowClear>
                <Select.Option value="Single">Single</Select.Option>
                <Select.Option value="Married">Married</Select.Option>
                <Select.Option value="Divorced">Divorced</Select.Option>
                <Select.Option value="Widowed">Widowed</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="current_location"
            label="Current Location"
          >
            <Input placeholder="Enter current location" />
          </Form.Item>

          <Form.Item
            name="emergency_contact_name"
            label="Emergency Contact Name"
          >
            <Input placeholder="Emergency contact person name" />
          </Form.Item>

          <Form.Item
            name="emergency_contact_number"
            label="Emergency Contact Number"
            rules={[
              { pattern: /^[\d+\-\s()]{10,15}$/, message: 'Please enter a valid phone number' }
            ]}
          >
            <Input placeholder="Emergency contact phone number" />
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" icon={<CheckOutlined />}>
                Create Patient
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Prescription List */}
      <PrescriptionList />
      
      {/* Prescription Details */}
      <PrescriptionDetails />
    </div>
  );
};

export default PatientSearch;
